/**
 * The app's on/off toggle: the same 44x26 green control the company page uses
 * to show and hide profile sections.
 *
 * Deliberately not `react-native`'s `Switch` — react-native-web renders that as
 * a checkbox that looks nothing like the rest of the UI, and its `trackColor`/
 * `thumbColor` props give no way to match the company-page toggle on web.
 */
import { useEffect, useRef } from 'react';
import { Animated, Platform, Pressable, StyleSheet } from 'react-native';
import { motion } from '@tabbio/tokens';
import { useTheme } from '../../hooks/useTheme';
import { useLocale } from '../../hooks/useLocale';
import { useHaptics } from '../../hooks/useHaptics';

/** Company-page section toggle green — keep in sync with PublicProfileBuilderScreen. */
const ON_COLOR = '#22C55E';
const THUMB_TRAVEL = 18;

export function AppSwitch({
  value,
  onValueChange,
  accessibilityLabel,
  disabled = false,
}: {
  value: boolean;
  onValueChange: (next: boolean) => void;
  accessibilityLabel?: string;
  disabled?: boolean;
}) {
  const { theme } = useTheme();
  const { isRTL } = useLocale();
  const haptics = useHaptics();
  const progress = useRef(new Animated.Value(value ? 1 : 0)).current;

  useEffect(() => {
    Animated.timing(progress, {
      toValue: value ? 1 : 0,
      duration: motion.duration.fast,
      // The track colour interpolates, which the native driver cannot do.
      useNativeDriver: false,
    }).start();
  }, [progress, value]);

  const toggle = () => {
    if (disabled) return;
    haptics.trigger('selection');
    onValueChange(!value);
  };

  // react-native-web only maps Space to a press for button-ish roles, so a
  // `role="switch"` would scroll the page instead of toggling. `onKeyDown` is
  // forwarded through Pressable to the DOM node but is absent from RN's types.
  const webKeyboardProps =
    Platform.OS === 'web' && !disabled
      ? ({
          onKeyDown: (event: { key?: string; preventDefault?: () => void }) => {
            if (event.key === ' ' || event.key === 'Spacebar') {
              event.preventDefault?.();
              toggle();
            }
          },
        } as object)
      : null;

  return (
    <Pressable
      onPress={toggle}
      disabled={disabled}
      accessibilityRole="switch"
      accessibilityState={{ checked: value, disabled }}
      // react-native-web drops `accessibilityState`, so the switch would
      // announce with no state. `aria-checked` is forwarded on both platforms.
      aria-checked={value}
      accessibilityLabel={accessibilityLabel}
      hitSlop={8}
      style={[styles.pressable, disabled ? styles.pressableDisabled : null]}
      {...webKeyboardProps}
    >
      <Animated.View
        style={[
          styles.track,
          {
            backgroundColor: progress.interpolate({
              inputRange: [0, 1],
              outputRange: [theme.surfaceTertiary, ON_COLOR],
            }),
          },
        ]}
      >
        <Animated.View
          style={[
            styles.thumb,
            {
              transform: [
                {
                  translateX: progress.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, isRTL ? -THUMB_TRAVEL : THUMB_TRAVEL],
                  }),
                },
              ],
            },
          ]}
        />
      </Animated.View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  pressable: {
    flexShrink: 0,
  },
  pressableDisabled: {
    opacity: 0.5,
  },
  track: {
    width: 44,
    height: 26,
    borderRadius: 999,
    padding: 2,
    justifyContent: 'center',
  },
  thumb: {
    width: 22,
    height: 22,
    borderRadius: 11,
    // White in both themes, matching the company-page toggle on web.
    backgroundColor: '#FFFFFF',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.18,
    shadowRadius: 2,
    elevation: 1,
  },
});
